import { useEffect } from "react";

const SITE_URL = "https://mj.lv";

interface PageSEOOptions {
  title: string;
  description: string;
  /** Language-neutral path, always without the /en prefix, e.g. "/ventilacija" */
  path: string;
  /** Current language — decides which URL is canonical for this render */
  lang?: "lv" | "en";
  /** Keep this page out of the index (404s, admin) */
  noindex?: boolean;
}

/** Creates the tag on first use, then just updates it on later renders. */
const upsert = (
  selector: string,
  create: () => HTMLElement,
  apply: (el: HTMLElement) => void,
) => {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  apply(el);
};

const meta = (attr: "name" | "property", key: string, content: string) =>
  upsert(
    `meta[${attr}="${key}"]`,
    () => {
      const m = document.createElement("meta");
      m.setAttribute(attr, key);
      return m;
    },
    (el) => el.setAttribute("content", content),
  );

const link = (rel: string, href: string, hreflang?: string) =>
  upsert(
    hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`,
    () => {
      const l = document.createElement("link");
      l.setAttribute("rel", rel);
      if (hreflang) l.setAttribute("hreflang", hreflang);
      return l;
    },
    (el) => el.setAttribute("href", href),
  );

export const usePageSEO = ({ title, description, path, lang = "lv", noindex = false }: PageSEOOptions) => {
  useEffect(() => {
    // Normalise so /ventilacija, /ventilacija/ and /Ventilacija all declare the
    // same canonical URL rather than presenting as three separate pages.
    const normalized = path.replace(/\/+$/, "").toLowerCase();
    const clean = normalized === "" || normalized === "/" ? "" : normalized;
    const lvUrl = `${SITE_URL}${clean}`;
    const enUrl = `${SITE_URL}/en${clean}`;
    const canonicalUrl = lang === "en" ? enUrl : lvUrl;

    document.title = title;

    meta("name", "description", description);
    link("canonical", canonicalUrl);

    meta("property", "og:title", title);
    meta("property", "og:description", description);
    meta("property", "og:url", canonicalUrl);
    meta("property", "og:locale", lang === "en" ? "en_US" : "lv_LV");
    meta("name", "twitter:title", title);
    meta("name", "twitter:description", description);

    // hreflang: each page points at both language versions of itself, including
    // a self-reference — without it search engines discard the whole cluster.
    link("alternate", lvUrl, "lv");
    link("alternate", enUrl, "en");
    link("alternate", lvUrl, "x-default");

    if (noindex) {
      meta("name", "robots", "noindex, follow");
    } else {
      document.head.querySelector('meta[name="robots"]')?.remove();
    }
  }, [title, description, path, lang, noindex]);
};
